/**
 * NotificationBell Component
 *
 * Bell icon button with unread notification badge.
 * Clicking the bell toggles the notification dropdown.
 *
 * Features:
 * - Polls for unread count every 30 seconds
 * - Shows badge with unread count (max display: 99+)
 * - Toggles dropdown on click
 *
 * @module components/NotificationBell
 */

import { useState, useRef, useEffect } from 'react';
import { NotificationDropdown } from './NotificationDropdown';
import {
  useUnreadNotificationCountGraphQL,
  useNotificationsGraphQL,
  useMarkNotificationReadGraphQL,
} from '@/graphql/hooks/useNotificationsGraphQL';
import logger from '@/utils/logger';
import { cn } from '@/lib/utils';
import { showNotificationToast } from '@/utils/toast';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '@/types/notification';

export interface NotificationBellProps {
  className?: string;
}

/**
 * NotificationBell - Bell icon with unread badge and dropdown
 *
 * Usage:
 *   <NotificationBell />
 */
export function NotificationBell({ className }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // GraphQL mutation for marking notifications as read
  const { markRead } = useMarkNotificationReadGraphQL();

  // Track which notifications we've already shown toasts for
  const shownToastsRef = useRef<Set<string>>(new Set());

  // Poll for unread count every 30 seconds using GraphQL
  const { count: unreadCount, refetch: refetchUnreadCount } = useUnreadNotificationCountGraphQL({
    pollInterval: 30000, // 30 seconds
  });

  // Poll for notifications to detect new high-urgency ones for toasts
  const { notifications, refetch: refetchNotifications } = useNotificationsGraphQL({
    limit: 10, // Only need recent notifications
    pollInterval: 30000, // 30 seconds (same as unread count)
  });

  // Show toasts for new high-urgency notifications
  useEffect(() => {
    if (!notifications || notifications.length === 0) {
      return;
    }

    // Filter for high-urgency unread notifications we haven't shown yet
    const newHighUrgencyNotifications = notifications.filter(
      (n: Notification) =>
        n.urgency === 'high' &&
        n.status === 'unread' &&
        !shownToastsRef.current.has(n.globalId)
    );

    // Show toast for each new high-urgency notification
    newHighUrgencyNotifications.forEach((notification: Notification) => {
      logger.info('Toast', 'Triggering toast for high-urgency notification', {
        globalId: notification.globalId,
        type: notification.notificationType,
      });

      showNotificationToast(
        notification,
        (actionUrl: string) => {
          // Navigate to the action URL when toast action button is clicked
          navigate(actionUrl);
        },
        async (globalId: string) => {
          // Mark notification as read when toast is dismissed or auto-closes
          try {
            logger.info('Notifications', 'Marking notification as read from toast dismiss via GraphQL', {
              globalId,
            });
            await markRead(globalId);

            // Refetch to refresh unread count and notification list
            await Promise.all([refetchUnreadCount(), refetchNotifications()]);

            logger.debug('Notifications', 'Notification marked as read via GraphQL and queries refetched', {
              globalId,
            });
          } catch (error) {
            logger.error('Notifications', 'Failed to mark notification as read via GraphQL', {
              globalId,
              error,
            });
          }
        }
      );

      // Mark this notification as shown
      shownToastsRef.current.add(notification.globalId);
    });

    // Cleanup: limit the Set size to prevent memory leaks over long sessions
    // Keep only the most recent 100 notification IDs
    if (shownToastsRef.current.size > 100) {
      const entries = Array.from(shownToastsRef.current);
      shownToastsRef.current = new Set(entries.slice(-100));
    }
  }, [notifications, navigate, markRead, refetchUnreadCount, refetchNotifications]);

  const handleClick = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    logger.info('Notifications', 'Bell clicked', { isOpen: newIsOpen, unreadCount });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (isOpen) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Format unread count for display (max 99+)
  const displayCount = unreadCount > 99 ? '99+' : unreadCount.toString();

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        onClick={handleClick}
        className={cn(
          'relative p-2 rounded-full transition-colors',
          'hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-primary',
          isOpen && 'bg-surface-2'
        )}
        aria-label={`Notifications (${unreadCount} unread)`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Bell Icon (SVG) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute top-0 right-0',
              'inline-flex items-center justify-center',
              'min-w-[20px] h-5 px-1',
              'text-xs font-bold text-white',
              'bg-danger rounded-full',
              'border-2 border-surface-1'
            )}
            aria-label={`${unreadCount} unread notifications`}
          >
            {displayCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <NotificationDropdown isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  );
}

export default NotificationBell;
